import { useCallback } from "preact/hooks";
import { getItem, slotOf, type EquipSlot } from "@/content/items";
import { QUESTS } from "@/content/quests";
import { lt, t } from "@/i18n";
import { accuracyOf, topicsForLevel } from "@/math";
import { returnToTitle } from "@/state/session";
import {
  backpackTab,
  equipItem,
  hero,
  heroStats,
  panel,
  removeItem,
  showToast,
  unequipSlot,
} from "@/state/store";
import { useKeyNav } from "./useKeyNav";

type Tab = "equipment" | "quests" | "progress";
const TABS: Tab[] = ["equipment", "quests", "progress"];

interface Action {
  label: string;
  run: () => void;
  primary?: boolean;
  disabled?: boolean;
}

export function BackpackPanel() {
  const tab = backpackTab;
  const h = hero.value;
  const prevTab = useCallback(
    () => (tab.value = TABS[(TABS.indexOf(tab.value) + TABS.length - 1) % TABS.length] as Tab),
    [],
  );
  const nextTab = useCallback(
    () => (tab.value = TABS[(TABS.indexOf(tab.value) + 1) % TABS.length] as Tab),
    [],
  );
  return (
    <div class="backpack">
      <div class="backpack-top">
        <span class="title">🎒 {t("backpack.title")}</span>
        <div class="tabs">
          {TABS.map((id) => (
            <button key={id} class={tab.value === id ? "on" : ""} onClick={() => (tab.value = id)}>
              {t(`backpack.tab.${id}`)}
            </button>
          ))}
        </div>
        <button class="menu" onClick={returnToTitle}>
          🏠 {t("backpack.menu")} (M)
        </button>
        <button class="close" onClick={() => (panel.value = null)}>
          {t("backpack.close")}
        </button>
      </div>
      {tab.value === "equipment" && <EquipmentTab onLeft={prevTab} onRight={nextTab} />}
      {tab.value === "quests" && <QuestsTab onLeft={prevTab} onRight={nextTab} />}
      {tab.value === "progress" && <ProgressTab onLeft={prevTab} onRight={nextTab} />}
      <div class="backpack-foot">
        {h.name} · {t("hud.lv")} {h.lvl} · {h.hp}/{h.hpMax} HP · 💰 {h.gold} · {t("keys.tabs")}
      </div>
    </div>
  );
}

function EquipmentTab({ onLeft, onRight }: { onLeft: () => void; onRight: () => void }) {
  const h = hero.value;
  const stats = heroStats(h);
  const slots: EquipSlot[] = ["weapon", "armor", "accessory"];
  const inventory = Object.entries(h.inventory).filter(([, n]) => n > 0);

  const use = (itemId: string) => {
    const item = getItem(itemId);
    if (item.heal) {
      if (h.hp >= h.hpMax) return;
      const without = removeItem(h, itemId);
      if (!without) return;
      hero.value = { ...without, hp: Math.min(h.hpMax, h.hp + item.heal) };
      showToast(t("battle.potionUsed", { n: Math.min(item.heal, h.hpMax - h.hp) }));
    }
  };

  // one keyboard-navigable action per row: unequip a slot, equip or use an item
  const actions: Action[] = [];
  for (const slot of slots) {
    if (h.equipment[slot])
      actions.push({ label: t("backpack.unequip"), run: () => (hero.value = unequipSlot(hero.value, slot)) });
  }
  for (const [id] of inventory) {
    const item = getItem(id);
    if (slotOf(item))
      actions.push({
        label: t("backpack.equip"),
        run: () => (hero.value = equipItem(hero.value, id)),
        primary: true,
      });
    else if (item.heal)
      actions.push({ label: t("backpack.use"), run: () => use(id), disabled: h.hp >= h.hpMax });
    else actions.push({ label: "", run: () => undefined, disabled: true });
  }
  const activate = useCallback(
    (i: number) => {
      const a = actions[i];
      if (a && !a.disabled) a.run();
    },
    [actions],
  );
  const selected = useKeyNav({ count: actions.length, onActivate: activate, onLeft, onRight, digits: false });

  let row = 0;
  const rowClass = () => (row++ === selected.value ? "selected" : "");

  return (
    <div class="equipment">
      <div class="slots">
        {slots.map((slot) => {
          const id = h.equipment[slot];
          const item = id ? getItem(id) : null;
          const cls = item ? rowClass() : "";
          return (
            <div key={slot} class={`slot ${cls}`}>
              <div class="slot-name">{t(`backpack.slot.${slot}`)}</div>
              {item ? (
                <>
                  <div class="slot-item">
                    <span class="icon">{item.icon}</span> {lt(item.name)}
                  </div>
                  <div class="desc">{lt(item.description)}</div>
                  <button onClick={() => (hero.value = unequipSlot(h, slot))}>{t("backpack.unequip")}</button>
                </>
              ) : (
                <div class="slot-empty">{t("backpack.empty")}</div>
              )}
            </div>
          );
        })}
      </div>
      <div class="stats-line">
        {t("backpack.stats", {
          atk: stats.atk,
          def: stats.def,
          time: stats.timerBonusSec,
          hints: stats.freeHints,
        })}
      </div>
      <div class="inventory">
        {inventory.length === 0 && <div class="empty">{t("backpack.noItems")}</div>}
        {inventory.map(([id, n]) => {
          const item = getItem(id);
          const slot = slotOf(item);
          const cls = rowClass();
          return (
            <div key={id} class={`inv-row ${cls}`}>
              <span class="icon">{item.icon}</span>
              <div class="info">
                <div class="name">
                  {lt(item.name)} <span class="count">×{n}</span>
                </div>
                <div class="desc">{lt(item.description)}</div>
              </div>
              {slot && (
                <button class="primary" onClick={() => (hero.value = equipItem(h, id))}>
                  {t("backpack.equip")}
                </button>
              )}
              {item.heal && (
                <button disabled={h.hp >= h.hpMax} onClick={() => use(id)}>
                  {t("backpack.use")}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuestsTab({ onLeft, onRight }: { onLeft: () => void; onRight: () => void }) {
  const h = hero.value;
  useKeyNav({ count: 0, onActivate: () => undefined, onLeft, onRight, digits: false });
  const entries = Object.values(QUESTS).filter((q) => h.quests[q.id]);
  return (
    <div class="quests">
      {entries.length === 0 && <div class="empty">{t("backpack.noQuests")}</div>}
      {entries.map((q) => {
        const p = h.quests[q.id]!;
        const done = p.status === "done";
        return (
          <div key={q.id} class={`quest ${done ? "done" : ""}`}>
            <div class="name">
              {done ? "✅" : "📜"} {lt(q.title)}
              <span class="status">{done ? t("backpack.questDone") : t("backpack.questActive")}</span>
            </div>
            <div class="desc">{lt(q.offer[0]!)}</div>
            {!done && (
              <div class="progress">
                {t("quest.progressShort", { progress: p.progress, count: q.objective.count })}
                <span class="reward">
                  · +{q.reward.xp} {t("hud.xp")}, +{q.reward.gold} 💰
                  {q.reward.items &&
                    Object.keys(q.reward.items).map((id) => ` , ${getItem(id).icon} ${lt(getItem(id).name)}`)}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ProgressTab({ onLeft, onRight }: { onLeft: () => void; onRight: () => void }) {
  const h = hero.value;
  const topics = topicsForLevel(h.level);
  const any = topics.some((td) => (h.stats[td.id]?.attempts ?? 0) > 0);
  const actions = [() => (panel.value = "report-history"), () => (panel.value = "report-curriculum")];
  const activate = useCallback((i: number) => actions[i]?.(), []);
  const selected = useKeyNav({ count: 2, onActivate: activate, onLeft, onRight });
  return (
    <div class="progress-tab">
      <div class="report-buttons">
        <button class={selected.value === 0 ? "selected" : ""} onClick={actions[0]}>
          <kbd>1</kbd> 🖨 {t("backpack.report")}
        </button>
        <button class={selected.value === 1 ? "selected" : ""} onClick={actions[1]}>
          <kbd>2</kbd> 📄 {t("backpack.curriculum")}
        </button>
      </div>
      {!any && <div class="empty">{t("backpack.noProgress")}</div>}
      {topics.map((td) => {
        const s = h.stats[td.id];
        if (!s || s.attempts === 0) return null;
        const pct = Math.round((s.correct / s.attempts) * 100);
        const weak = s.attempts >= 5 && accuracyOf(s) < 0.6;
        return (
          <div key={td.id} class="topic-row">
            <div class="name">{lt(td.name)}</div>
            <div class="bar">
              <div class={`fill ${weak ? "weak" : ""}`} style={{ width: `${pct}%` }} />
            </div>
            <div class="pct">{t("backpack.accuracy", { pct, correct: s.correct, attempts: s.attempts })}</div>
          </div>
        );
      })}
      <div class="totals">
        {t("backpack.totals", { answered: h.answered, correct: h.correct, best: h.bestStreak })}
      </div>
    </div>
  );
}
